import { useLayoutEffect, useRef } from 'react';

/**
 * Sets the element height to fill the available viewport space from its top
 * position down to the bottom, minus bottomPadding. Constrains between min and max.
 * Also sets overflow:auto to ensure inner scrolling while keeping parents static.
 */
export function useAutoHeight<T extends HTMLElement>(
  bottomPadding: number = 16,
  minHeight: number = 320,
  maxHeight: number = 1200,
) {
  const ref = useRef<T | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      // flex-basis: 0% (from "flex: 1") makes flex-grow ignore the height set below.
      el.style.flexBasis = 'auto';
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const rect = el.getBoundingClientRect();
      let height = Math.floor(viewport - rect.top - bottomPadding);
      if (Number.isFinite(minHeight)) height = Math.max(minHeight, height);
      if (Number.isFinite(maxHeight)) height = Math.min(maxHeight, height);
      el.style.height = `${height}px`;
      el.style.overflow = 'auto';
      el.style.boxSizing = 'border-box';
      // fine-tune to eliminate trailing gap or overflow by re-measuring
      const after = el.getBoundingClientRect();
      const delta = Math.round(viewport - (after.top + after.height) - bottomPadding);
      if (delta !== 0) {
        const adjusted = Math.max(minHeight, Math.min(maxHeight, height + delta));
        if (adjusted !== height) {
          el.style.height = `${adjusted}px`;
        }
      }
    };

    update();
    window.addEventListener('resize', update);
    const ro = new ResizeObserver(update);
    ro.observe(document.body);

    return () => {
      window.removeEventListener('resize', update);
      ro.disconnect();
    };
  }, [bottomPadding, minHeight, maxHeight]);

  return ref;
}
